import { BookOpen, Brain } from "lucide-react";
import { cn } from "@/utils";
import { TooltipRoot, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip/Tooltip";

export type LinkIconVariant = "kb" | "kg";

export interface LinkIconProps {
  /** Identifies the linked resource type — drives the icon glyph (BookOpen for
   *  KB, Brain for KG). Glyph stays consistent regardless of disabled state so
   *  KB and KG never collapse to the same visual when both are unavailable. */
  variant: LinkIconVariant;
  /** Tooltip + aria-label text. Required for a11y. e.g. "47 entities · 89 relations" */
  title: string;
  onClick?: () => void;
  /** When true, renders muted styling and blocks click. Glyph is unchanged. */
  disabled?: boolean;
  className?: string;
}

// LinkIcon is rendered inside clickable parents (cards, rows). Click events must
// not bubble to the parent or both the LinkIcon's navigation and the parent's
// drawer-open will fire on a single user click.

const activeStyles: Record<LinkIconVariant, string> = {
  kb: "bg-[var(--primary-100)] text-[var(--primary-700)] cursor-pointer hover:scale-[1.12]",
  kg: "bg-[var(--purple-bg)] text-[var(--purple)] cursor-pointer hover:scale-[1.12]",
};

const disabledStyles =
  "bg-[var(--muted)] text-[var(--muted-foreground)] opacity-60 cursor-not-allowed";

const VariantIcon: Record<LinkIconVariant, React.ReactNode> = {
  kb: <BookOpen size={12} strokeWidth={1.6} aria-hidden />,
  kg: <Brain size={12} strokeWidth={1.6} aria-hidden />,
};

/**
 * LinkIcon — 22×22 round icon-pill for linked resources (KB, KG).
 * Spec: 22px, radius-full, 12px icon, scale(1.12) hover 120ms ease-standard.
 * Disabled blocks click + shows muted styling, but keeps the variant's glyph.
 */
export function LinkIcon({ variant, title, onClick, disabled = false, className }: LinkIconProps) {
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!disabled && onClick) onClick();
  };

  return (
    <TooltipRoot>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={title}
          disabled={disabled}
          onClick={handleClick}
          className={cn(
            "inline-flex items-center justify-center",
            "w-[22px] h-[22px] rounded-[var(--radius-full)]",
            "transition-transform duration-[120ms] [transition-timing-function:cubic-bezier(0.2,0,0,1)]",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)]",
            "border-0 p-0 shrink-0",
            disabled ? disabledStyles : activeStyles[variant],
            className
          )}
        >
          {VariantIcon[variant]}
        </button>
      </TooltipTrigger>
      <TooltipContent>{title}</TooltipContent>
    </TooltipRoot>
  );
}
